import { lazy } from 'react'
import generateRouterDetail from 'utils/helpers/generateRouterDetail'
import { SkuRegistrationPageRBAC } from 'features/Product/pages/SkuRegistration/rbac'
import { ProductLabelRBAC } from 'features/Product/pages/ProductLabel/rbac'
import { PackageStructurePageRBAC } from 'features/Product/pages/PackageStructure/rbac'
import { WarehouseListRBAC } from 'features/Product/pages/Warehouse/rbac'
import { HubListRBAC } from 'features/Product/pages/Hub/rbac'
import {
  ProductStockHistoryRBAC,
  ProductStockHistoryHubRBAC,
} from 'features/Product/pages/ProductStockHistory/rbac'
import { WarehouseInventoryDetailRBAC } from 'features/Product/pages/InventoryDetail/rbac'

import ModifierRouterList, { ModifierNamedRoutesType, ModifierPathListsType } from './modifier'
import ProductMasterDetailRouters, {
  ProductMasterNamedRoutesType,
  ProductMasterPathListsType,
} from './productDetail'
import SEORouterList, { SEONamedRoutesType, SEOPathListsType } from './seoRouter'
import { MasterVariantPageRBAC } from '../pages/MasterVariant/rbac'
import { ProductVariantPageRBAC } from '../pages/ProductVariant/rbac'
import {
  AddProductVariantPageRBAC,
  EditProductVariantPageRBAC,
} from '../pages/AddProductVariant/rbac'
import { ProductListPageRBAC } from '../pages/Products/rbac'
import { ProductMasterCreatePageRBAC } from '../pages/ProductAdd/rbac'
import { ConversionRuleRBAC } from '../pages/ConversionRule/rbac'
import { ConversionTaskRBAC } from '../pages/ConversionTask/rbac'
import { ConversionTaskDetailRBAC } from '../pages/ConversionTaskDetail/rbac'

type NamedRoutesType =
  | 'VariantModuleRouter'
  | 'ProductVariantRouter'
  | 'AddProductVariantRouter'
  | 'EditProductVariantRouter'
  | 'MasterVariantRouter'
  | 'ConversionRuleRouter'
  | 'ConversionTaskRouter'
  | 'ConversionTaskDetailRouter'
  | ModifierNamedRoutesType
  | 'PriceTagRouter'
  | 'HubProductStockHistory'
  | 'WarehouseProductStockHistory'
  | 'SkuRegistrationList'
  | 'ProductLabel'
  | 'ProductAddRouter'
  | ProductMasterNamedRoutesType
  | 'ProductRouter'
  | 'PackageStructureList'
  | 'WarehouseRouter'
  | 'HubRouter'
  | 'InventoryDetailRouter'
  | SEONamedRoutesType

type PathListsType =
  | '/dashboard/product-variant'
  | '/dashboard/product-variant/add'
  | '/dashboard/product-variant/edit/:productVariantId'
  | '/dashboard/master-variant'
  | '/dashboard/conversion-rule'
  | '/dashboard/conversion-task'
  | '/dashboard/conversion-task/:id'
  | ModifierPathListsType
  | '/dashboard/price-tag'
  | '/dashboard/hub/stock-detail'
  | '/dashboard/warehouse/stock-detail'
  | '/dashboard/sku-registration'
  | '/dashboard/product-label'
  | '/dashboard/product/add'
  | ProductMasterPathListsType
  | '/dashboard/product'
  | '/dashboard/package-structure/:id'
  | '/dashboard/warehouse'
  | '/dashboard/hub'
  | '/dashboard/warehouse/inventory-detail'
  | SEOPathListsType

type RouterInstanceType = RouterFeatureInterface<NamedRoutesType, PathListsType>

const ProductVariantPage = lazy(() => import('../pages/ProductVariant'))
const AddProductVariantPage = lazy(() => import('../pages/AddProductVariant'))
const MasterVariant = lazy(() => import('../pages/MasterVariant'))
const ConversionRule = lazy(() => import('../pages/ConversionRule'))
const ConversionTask = lazy(() => import('../pages/ConversionTask'))
const ConversionTaskDetail = lazy(() => import('../pages/ConversionTaskDetail'))
const PriceTag = lazy(() => import('../pages/PriceTag'))
const ProductStockHistory = lazy(() => import('../pages/ProductStockHistory'))
const SkuRegistration = lazy(() => import('../pages/SkuRegistration'))
const ProductLabel = lazy(() => import('../pages/ProductLabel'))
const ProductAddPage = lazy(() => import('../pages/ProductAdd'))
const ProductPage = lazy(() => import('../pages/Products'))
const PackageStructure = lazy(() => import('../pages/PackageStructure'))
const Warehouse = lazy(() => import('../pages/Warehouse'))
const Hub = lazy(() => import('../pages/Hub'))
const InventoryDetail = lazy(() => import('../pages/InventoryDetail'))

export const VariantModuleRouter: RouterInstanceType = {
  name: 'VariantModuleRouter',
  Component: ProductVariantPage,
  path: '/dashboard/product-variant',
  routeType: 'PRIVATE',
  allowedRoles: [
    'AUTH_PRODUCT_MANAGER',
    'AUTH_MERCHANDISING_WAREHOUSE',
    'AUTH_MERCHANDISING_HUB',
    'AUTH_HO_MERCHANDISING',
  ],
  isDashboard: false,
}

const ProductVariantRouter: RouterInstanceType = {
  name: 'ProductVariantRouter',
  Component: ProductVariantPage,
  path: '/dashboard/product-variant',
  routeType: 'PRIVATE',
  allowedRoles: [
    // 'AUTH_PRODUCT_MANAGER', 'AUTH_MERCHANDISING_WAREHOUSE', 'AUTH_MERCHANDISING_HUB'
  ],
  isDashboard: true,
  rbacPageID: ProductVariantPageRBAC.pageID,
}

const AddProductVariantRouter: RouterInstanceType = {
  name: 'AddProductVariantRouter',
  Component: AddProductVariantPage,
  path: '/dashboard/product-variant/add',
  routeType: 'PRIVATE',
  allowedRoles: [
    // 'AUTH_PRODUCT_MANAGER', 'AUTH_MERCHANDISING_WAREHOUSE', 'AUTH_MERCHANDISING_HUB'
  ],
  isDashboard: true,
  rbacPageID: AddProductVariantPageRBAC.pageID,
}

const EditProductVariantRouter: RouterInstanceType = {
  name: 'EditProductVariantRouter',
  Component: AddProductVariantPage,
  path: '/dashboard/product-variant/edit/:productVariantId',
  routeType: 'PRIVATE',
  allowedRoles: [
    // 'AUTH_PRODUCT_MANAGER', 'AUTH_MERCHANDISING_WAREHOUSE', 'AUTH_MERCHANDISING_HUB'
  ],
  isDashboard: true,
  rbacPageID: EditProductVariantPageRBAC.pageID,
}

const MasterVariantRouter: RouterInstanceType = {
  name: 'MasterVariantRouter',
  Component: MasterVariant,
  path: '/dashboard/master-variant',
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: true,
  rbacPageID: MasterVariantPageRBAC.pageID,
}

const ConversionRuleRouter: RouterInstanceType = {
  name: 'ConversionRuleRouter',
  Component: ConversionRule,
  path: '/dashboard/conversion-rule',
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: true,
  rbacPageID: ConversionRuleRBAC.pageID,
}

const ConversionTaskRouter: RouterInstanceType = {
  name: 'ConversionTaskRouter',
  Component: ConversionTask,
  path: '/dashboard/conversion-task',
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: true,
  rbacPageID: ConversionTaskRBAC.pageID,
}

const ConversionTaskDetailRouter: RouterInstanceType = {
  name: 'ConversionTaskDetailRouter',
  Component: ConversionTaskDetail,
  path: '/dashboard/conversion-task/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: true,
  rbacPageID: ConversionTaskDetailRBAC.pageID,
}

const PriceTagRouter: RouterInstanceType = {
  name: 'PriceTagRouter',
  Component: PriceTag,
  path: '/dashboard/price-tag',
  routeType: 'PRIVATE',
  allowedRoles: [
    'AUTH_ASTRO_GO_MANAGER',
    'AUTH_ASTRO_GO_SPV',
    'AUTH_ASTRO_GO_SHIFT_LEAD',
    'AUTH_ASTRO_GO_STAFF',
  ],
  isDashboard: true,
}

const HubProductStockHistoryRouter: RouterInstanceType = {
  name: 'HubProductStockHistory',
  Component: ProductStockHistory,
  path: '/dashboard/hub/stock-detail',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ProductStockHistoryHubRBAC.pageID,
  isDashboard: true,
}

const WarehouseProductStockHistoryRouter: RouterInstanceType = {
  name: 'WarehouseProductStockHistory',
  Component: ProductStockHistory,
  path: '/dashboard/warehouse/stock-detail',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ProductStockHistoryRBAC.pageID,
  isDashboard: true,
}

const PackageStructureRouter: RouterInstanceType = {
  name: 'PackageStructureList',
  Component: PackageStructure,
  path: '/dashboard/package-structure/:id',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: PackageStructurePageRBAC.pageID,
  isDashboard: true,
}

const SkuRegistrationListRouter: RouterInstanceType = {
  name: 'SkuRegistrationList',
  Component: SkuRegistration,
  path: '/dashboard/sku-registration',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: SkuRegistrationPageRBAC.pageID,
  isDashboard: true,
}

const ProductLabelRouter: RouterInstanceType = {
  name: 'ProductLabel',
  Component: ProductLabel,
  path: '/dashboard/product-label',
  routeType: 'PRIVATE',
  allowedRoles: [],
  isDashboard: true,
  rbacPageID: ProductLabelRBAC.pageID,
}

const ProductAddRouter: RouterInstanceType = {
  name: 'ProductAddRouter',
  Component: ProductAddPage,
  path: '/dashboard/product/add',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ProductMasterCreatePageRBAC.pageID,
  isDashboard: true,
}

const ProductsRouter: RouterInstanceType = {
  name: 'ProductRouter',
  Component: ProductPage,
  path: '/dashboard/product',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: ProductListPageRBAC.pageID,
  isDashboard: true,
}

const WarehouseRouter: RouterInstanceType = {
  name: 'WarehouseRouter',
  Component: Warehouse,
  path: '/dashboard/warehouse',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: WarehouseListRBAC.pageID,
  isDashboard: true,
}

const HubRouter: RouterInstanceType = {
  name: 'HubRouter',
  Component: Hub,
  path: '/dashboard/hub',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: HubListRBAC.pageID,
  isDashboard: true,
}

const InventoryDetailRouter: RouterInstanceType = {
  name: 'InventoryDetailRouter',
  Component: InventoryDetail,
  path: '/dashboard/warehouse/inventory-detail',
  routeType: 'PRIVATE',
  allowedRoles: [],
  rbacPageID: WarehouseInventoryDetailRBAC.pageID,
  isDashboard: true,
}

const ProductRouter: RouterInstanceType[] = [
  ProductVariantRouter,
  AddProductVariantRouter,
  EditProductVariantRouter,
  MasterVariantRouter,
  ConversionRuleRouter,
  ConversionTaskRouter,
  ConversionTaskDetailRouter,
  ...ModifierRouterList,
  PriceTagRouter,
  HubProductStockHistoryRouter,
  WarehouseProductStockHistoryRouter,
  SkuRegistrationListRouter,
  ProductLabelRouter,
  ProductAddRouter,
  ProductsRouter,
  ...ProductMasterDetailRouters,
  PackageStructureRouter,
  WarehouseRouter,
  HubRouter,
  InventoryDetailRouter,
  ...SEORouterList,
]

export const ProductRouterDetail = generateRouterDetail(ProductRouter)

export default ProductRouter
